/*
 **	SFC Calibration (c) 2026 Silver Falls Capital. All Rights Reserved
 **
 **	This code is unlicensed and may not be copied, used, modified,
 **	compiled, run, or distributed in any form without the explicit
 **	written permission of Silver Falls Capital.
 **
 **/

import { useEffect, useState } from 'react'
import { MsalProvider } from '@azure/msal-react'
import { PublicClientApplication, EventType } from '@azure/msal-browser'
import type { AuthenticationResult, EventMessage } from '@azure/msal-browser'

import { msalConfig } from '../lib/authConfig'
import LoadingPage from './LoadingPage'

/**
 * MSAL provider, following the house pattern in ~/projects/sfc/template.
 *
 * The instance is a true singleton on `window`, and initialisation runs exactly
 * once: React 18 StrictMode double-invokes effects, and a second
 * `handleRedirectPromise()` on the same instance loses the redirect response.
 */
const MSAL_INSTANCE_KEY = '__msalInstance__'

function getMsalInstance(): PublicClientApplication | null {
	if (typeof window === 'undefined') return null

	const existing = (window as any)[MSAL_INSTANCE_KEY]
	if (existing) return existing

	const instance = new PublicClientApplication(msalConfig)
	;(window as any)[MSAL_INSTANCE_KEY] = instance

	instance.addEventCallback((event: EventMessage) => {
		if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
			const payload = event.payload as AuthenticationResult
			instance.setActiveAccount(payload.account)
		}
	})

	return instance
}

let initPromise: Promise<void> | null = null

function initializeMsal(instance: PublicClientApplication): Promise<void> {
	if (initPromise) return initPromise

	initPromise = instance
		.initialize()
		.then(() => instance.handleRedirectPromise())
		.then((response) => {
			if (response) {
				instance.setActiveAccount(response.account)
			}
		})
		.catch((error) => {
			console.error('Error initializing MSAL:', error)
		})

	return initPromise
}

export default function AuthProvider({ children }: { children: React.ReactNode }) {
	const [instance] = useState<PublicClientApplication | null>(() => getMsalInstance())
	const [ready, setReady] = useState(false)

	useEffect(() => {
		if (!instance) return
		initializeMsal(instance).then(() => setReady(true))
	}, [instance])

	if (!ready || !instance) {
		return <LoadingPage />
	}

	return <MsalProvider instance={instance}>{children}</MsalProvider>
}
